import React, { useRef, useEffect } from 'react';
import Map, { MapRef, ViewStateChangeEvent } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box } from '@mui/material';
import { BoundingBox } from '../interface/BoundingBox';
import { setPosition, toggleSatelliteView } from '../redux/mapSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { NavigationControl } from 'react-map-gl';
import { MapComponentProps } from '../interface/MapComponentProps';
import AttributionBox from './AttributionBox';

const MapComponent: React.FC<MapComponentProps> = ({
  onBoundingBoxChange,
  children,
}) => {
  const { REACT_APP_MAPTILER_KEY } = process.env;
  const dispatch = useDispatch();
  const position = useSelector((state: RootState) => state.map);
  const satelliteView = useSelector(
    (state: RootState) => state.map.satelliteView
  );

  const mapRef = useRef<MapRef>(null);
  const isUserInteracting = useRef(false);

  const toggleMapStyle = () => {
    dispatch(toggleSatelliteView());
  };

  const handleViewStateChange = (event: ViewStateChangeEvent) => {
    const newViewState = event.viewState;
    dispatch(setPosition(newViewState));
  };

  const roundCoordinate = (value: number) => parseFloat(value.toFixed(7));

  const updateBoundingBox = () => {
    if (mapRef.current) {
      const bounds = mapRef.current.getMap().getBounds();
      const boundingBox: BoundingBox = {
        min_longitude: roundCoordinate(bounds.getWest()),
        min_latitude: roundCoordinate(bounds.getSouth()),
        max_longitude: roundCoordinate(bounds.getEast()),
        max_latitude: roundCoordinate(bounds.getNorth()),
      };
      onBoundingBoxChange(boundingBox);
    }
  };

  useEffect(() => {
    if (!isUserInteracting.current) {
      const timeoutId = setTimeout(() => {
        updateBoundingBox();
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [position.latitude, position.longitude]);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Map
        id={'map'}
        {...position}
        onMoveStart={() => {
          isUserInteracting.current = true;
        }}
        onMove={handleViewStateChange}
        onMoveEnd={() => {
          isUserInteracting.current = false;
          updateBoundingBox();
        }}
        onLoad={updateBoundingBox}
        ref={mapRef}
        style={{ width: '100%', height: '100%' }}
        mapStyle={
          satelliteView
            ? `https://api.maptiler.com/maps/satellite/style.json?key=${REACT_APP_MAPTILER_KEY}`
            : `https://api.maptiler.com/maps/basic-v2/style.json?key=${REACT_APP_MAPTILER_KEY}`
        }
        attributionControl={false}
      >
        {children}
        <NavigationControl position="top-right" />
      </Map>
      <AttributionBox />

      <button
        id={'map-style-button'}
        onClick={toggleMapStyle}
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          background: 'none',
          border: '2px solid white',
          padding: 0,
          width: '70px',
          height: '70px',
          cursor: 'pointer',
        }}
      >
        <img
          src={satelliteView ? '/basic.png' : '/satellite.png'}
          alt="Toggle Map Style"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </button>
    </Box>
  );
};

export default MapComponent;
